/**
 * Conference Sales Service
 * Handles conference sales data processing for the ConferenceSalesCarousel
 */

import { formatCurrency } from './RevenueCalculationService';
import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Build configured conference sales years as numbers.
 * @returns {number[]}
 */
export const getConfiguredConferenceSalesYears = () => {
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year);
  }
  return years;
};

/**
 * Get sales data for a specific client and sponsor
 * @param {string} clientName - The client name
 * @param {string} sponsorName - The sponsor name
 * @param {Object} conferenceSalesData - The conference sales data from third sheet
 * @returns {Object|null} Sales data for the client-sponsor combination, or null if not found
 */
export const getSalesDataForSponsor = (clientName, sponsorName, conferenceSalesData) => {
  if (!conferenceSalesData || !clientName || !sponsorName) {
    return null;
  }

  // Navigate the nested structure: conferenceSalesData[clientName][sponsorName]
  const clientData = conferenceSalesData[clientName];
  if (!clientData) {
    return null;
  }

  const sponsorData = clientData[sponsorName];
  if (!sponsorData) {
    return null;
  }

  return sponsorData;
};

/**
 * Filter sales data by selected year
 * @param {Object} salesData - Sales data object with years as keys
 * @param {string} selectedYear - The selected year ('All' or specific year like '2024')
 * @returns {Array<Object>} Array of year data objects
 */
export const filterSalesDataByYear = (salesData, selectedYear) => {
  if (!salesData) {
    return [];
  }

  const years = getConfiguredConferenceSalesYears();

  // If "All Years" is selected, return all years (oldest to newest)
  if (selectedYear === 'All') {
    return years
      .filter(year => salesData[year]) // Only include years that exist in data
      .map(year => ({
        year,
        ...salesData[year]
      }));
  }

  // If specific year is selected, return only that year
  const yearNum = parseInt(selectedYear);
  if (salesData[yearNum]) {
    return [{
      year: yearNum,
      ...salesData[yearNum]
    }];
  }

  return [];
};

/**
 * Check if a year's data is all zeros (no sales)
 * @param {Object} yearData - Year data with rooms, rn, revenue
 * @returns {boolean} True if all values are 0 or missing
 */
const isNoSales = (yearData) => {
  const rooms = yearData.rooms || 0;
  const rn = yearData.rn || 0;
  const revenue = yearData.revenue || 0;

  return rooms === 0 && rn === 0 && revenue === 0;
};

/**
 * Format a single value for display, showing "No sales" if 0
 * @param {number} value - The value to format
 * @param {string} type - The type of value ('currency', 'number')
 * @returns {string} Formatted value or "No sales"
 */
const formatValue = (value, type) => {
  if (!value || value === 0) {
    return 'No sales';
  }

  if (type === 'currency') {
    return formatCurrency(value);
  }

  return value.toString();
};

/**
 * Generate slides for the ConferenceSalesCarousel
 * @param {string} clientName - The client name
 * @param {string} sponsorName - The current sponsor name
 * @param {string} selectedYear - The selected year filter
 * @param {Object} conferenceSalesData - The conference sales data
 * @returns {Array<Object>} Array of slide objects for the carousel
 */
export const generateConferenceSalesSlides = (
  clientName,
  sponsorName,
  selectedYear,
  conferenceSalesData
) => {
  // Get sales data for this client-sponsor combination
  const salesData = getSalesDataForSponsor(clientName, sponsorName, conferenceSalesData);

  if (!salesData) {
    // Return a single "No data" slide if no sales data found
    return [{
      year: selectedYear === 'All' ? 'All Years' : selectedYear,
      rooms: 'No sales',
      rn: 'No sales',
      revenue: 'No sales',
      isNoData: true
    }];
  }

  // Filter sales data by selected year
  const filteredData = filterSalesDataByYear(salesData, selectedYear);

  if (filteredData.length === 0) {
    // Return a single "No data" slide if no years match
    return [{
      year: selectedYear === 'All' ? 'All Years' : selectedYear,
      rooms: 'No sales',
      rn: 'No sales',
      revenue: 'No sales',
      isNoData: true
    }];
  }

  // Convert filtered data to slides format
  const slides = filteredData.map(yearData => {
    const hasNoSales = isNoSales(yearData);

    return {
      year: yearData.year.toString(),
      rooms: formatValue(yearData.rooms, 'number'),
      rn: formatValue(yearData.rn, 'number'),
      revenue: formatValue(yearData.revenue, 'currency'),
      isNoData: false,
      hasNoSales
    };
  });

  return slides;
};

/**
 * Get slide count for display in carousel pagination
 * @param {Array<Object>} slides - Array of slide objects
 * @returns {number} Number of slides
 */
export const getSlideCount = (slides) => {
  return slides ? slides.length : 0;
};
